import { TimelineItemContentBadgeTypeT } from 'components/Timeline/types'
import type { TimelineItemContentIdentificationT, TimelineItemContentT } from 'components/Timeline/types'
import type { TFunction } from 'i18next'
import type { TaskT, ShareUserDataTaskT } from 'pages/Tasks/types'
import { getAvatarFromPerson } from 'pages/Tasks/utils/timeline'
import { TIMELINE_STATUS } from 'redesign/components/organisms/timeline-item/constants'
import type { TimelineReminderProps } from 'redesign/components/organisms/timeline-reminder/types'
import { formatDate } from 'redesign/shared/date'
import { DATE_FORMAT_VARIANTS } from 'redesign/shared/date/constants'
import { absencePeriods, absencePeriodsTranslationMap, ZERO } from 'shared/constants'
import type { AbsenceData, TimelineItem } from 'shared/graphql/graphql'
import type { DocumentT, PersonT } from 'types'
import { getFullAvatarUrl } from 'utils/avatar'
import { daysIncludedInRange, newDateWithoutTime } from 'utils/date'
import { getAvatarName } from 'utils/person'
import { TASK_STATES, TASK_TYPES, TASKS_STATUS } from '../../ContentLists/TaskList/constants'
import type { TaskState } from '../../ContentLists/TaskList/types'
import { ABSENCE_REQUEST_BADGE_TYPES } from '../constants'
import type { TimelineItemAttributes } from '../types'
import { WORK_TIME_ACCEPTED_ID } from './constants'
import {
  createAvatarIcon,
  createClosedTimelineItemAttributes,
  createTimeLineItem,
  createTimelineItemAttributesWithHrAvatar,
  createWaitingTimelineItemAttributes,
  createWaitingUserTimelineItemAttributes,
} from './timeline-item-factory'
import type {
  ItsMeProps,
  MayAddItemProps,
  GetReminderProps,
  TimelineAttributesProps,
  TimelineContentProps,
  IdentificationCardProps,
  GetAbsenceDetails,
  TimelineItemAbsenceDetails,
} from './types'

const getAvatarFromTimeline = (timeline: TimelineItem, t: TFunction, itsMe = false) => {
  const userName = itsMe ? t('timeline.avatar.you') : `${timeline.authorFirstName} ${timeline.authorLastName}`
  const avatarName = getAvatarName(timeline.authorFirstName ?? '', timeline.authorLastName ?? '')
  const avatarUrl = timeline.authorAvatarUrl ? getFullAvatarUrl(timeline.authorAvatarUrl) : undefined

  return createAvatarIcon({ avatarName, avatarUrl, userName })
}

export const getItsMe = ({ authorId, myPersonIds }: ItsMeProps): boolean => {
  if (!authorId) return false

  const itsMe = myPersonIds.find((personId) => personId.toString() === authorId)

  return Boolean(itsMe)
}

export const mayAddPendingElement = ({ task, t, isOffice = false }: MayAddItemProps) => {
  const { statusFe, status } = task

  if (statusFe !== TASK_STATES.pending && statusFe !== TASK_STATES.todo) {
    return []
  }

  const id = isOffice ? 'waitingFrontlinerResponse' : 'waitingManagerResponse'
  const attributes = createWaitingTimelineItemAttributes({ id, t, isOffice, status })

  return [
    createTimeLineItem({
      attributes,
    }),
  ]
}

export const mayAddClosedElement = ({ task, t }: MayAddItemProps) => {
  if (task.statusFe !== TASK_STATES.closed) return []

  return [
    createTimeLineItem({
      attributes: {
        id: WORK_TIME_ACCEPTED_ID,
        title: t('timeline.statusText.closed'),
        status: TIMELINE_STATUS.closed,
      },
    }),
  ]
}

export const mayAddSharedDataAction = ({ task, t, locale, isOffice }: MayAddItemProps) => {
  const sharedUserDataTask = task as TaskT<ShareUserDataTaskT>

  if (sharedUserDataTask.taskData?.sentDataOn) {
    const date = formatDate({
      date: sharedUserDataTask.taskData.sentDataOn,
      dateFormat: DATE_FORMAT_VARIANTS.shortWithWeekdayAndTime,
      locale,
      t,
    })

    const avatar = getAvatarFromPerson(task?.person as PersonT, t, !isOffice)

    const sharedDataStatus = createTimeLineItem({
      attributes: {
        id: 'sharedDataStatus',
        title: date,
        avatarData: avatar,
        status: TIMELINE_STATUS.past,
      },
      content: {
        badge: TimelineItemContentBadgeTypeT.sharedPersonalData,
        text: sharedUserDataTask.taskData.userComment,
      },
    })

    return [sharedDataStatus]
  }

  return []
}

export const getAbsenceDetails = ({
  task,
  t,
  timelineItem,
}: GetAbsenceDetails): TimelineItemAbsenceDetails | undefined => {
  if (
    task.taskType !== TASK_TYPES.absenceRequest ||
    !ABSENCE_REQUEST_BADGE_TYPES.includes(timelineItem.badge as TimelineItemContentBadgeTypeT)
  ) {
    return undefined
  }

  const taskData = task.taskData as AbsenceData
  const { dateFrom, dateTo, dates = [] } = taskData

  if (!dateFrom || !dateTo) return undefined

  const isSingleDay = dateFrom === dateTo

  const getDayTime = (): string => {
    if (!isSingleDay || !dates || dates.length === ZERO || !dates[0]?.period) return ''

    const translatedKey = absencePeriodsTranslationMap[dates[0].period]

    return translatedKey ? t(absencePeriods[translatedKey]) : ''
  }

  const getAbsenceType = (): string => {
    if (isSingleDay) return ''

    const days = daysIncludedInRange(newDateWithoutTime(dateFrom), newDateWithoutTime(dateTo))

    return `${days} ${t('page.shifts.balance.days')}`
  }

  return {
    singleDay: isSingleDay,
    dayTime: getDayTime(),
    absenceType: getAbsenceType(),
    dateFrom: newDateWithoutTime(dateFrom),
    dateTo: newDateWithoutTime(dateTo),
    dates: taskData.dates,
  }
}

export const mayAddDefaultClosedAction = ({ task, t, locale, isOffice }: MayAddItemProps) => {
  if (task.status === TASKS_STATUS.approvedFrontline) {
    const taskDocument = task?.taskMonthlySheet?.document
    const date = taskDocument?.createdByMirus
      ? formatDate({
          date: taskDocument?.createdByMirus,
          dateFormat: DATE_FORMAT_VARIANTS.shortWithWeekdayAndTime,
          locale,
          t,
        })
      : ''

    const avatar = getAvatarFromPerson(task?.person as PersonT, t, !isOffice)

    const worktimeAcceptedStatus = createTimeLineItem({
      attributes: {
        id: 'worktimeAcceptedStatus',
        title: date,
        avatarData: avatar,
        status: TIMELINE_STATUS.closed,
      },
      content: { badge: TimelineItemContentBadgeTypeT.workTimeAccepted },
    })

    return [worktimeAcceptedStatus]
  }

  return []
}

const getSystemTimelineItem = ({
  timelineItem,
  t,
  locale,
  status,
  taskStatus,
  isOffice,
}: TimelineAttributesProps): TimelineItemAttributes => {
  const id = `timelineItemHeader-${timelineItem.id}`

  if (status === TIMELINE_STATUS.closed) {
    return createClosedTimelineItemAttributes({ id, t })
  }

  if (status === TIMELINE_STATUS.current) {
    // If the state is TODO the current user is the one who has to do the action
    return taskStatus === TASK_STATES.todo
      ? createWaitingUserTimelineItemAttributes({ id, t })
      : createWaitingTimelineItemAttributes({ id, t, isOffice })
  }

  const date = timelineItem?.insertedAt
    ? formatDate({
        date: timelineItem.insertedAt,
        locale,
        t,
        dateFormat: DATE_FORMAT_VARIANTS.shortWithWeekdayAndTime,
      })
    : ''

  return createTimelineItemAttributesWithHrAvatar(
    { id, title: date, status, badgeType: timelineItem.badge as TimelineItemContentBadgeTypeT },
    t,
  )
}

export const getTimelineAttributes = (props: TimelineAttributesProps) => {
  const { timelineItem, t, locale, itsMe, status } = props
  const id = `timelineItemHeader-${timelineItem.id}`

  if (timelineItem.authorIsSystem) {
    // TODO: Update date formats
    return getSystemTimelineItem(props)
  }

  const date = timelineItem?.insertedAt
    ? formatDate({ date: timelineItem.insertedAt, dateFormat: DATE_FORMAT_VARIANTS.shortWithWeekdayAndTime, locale, t })
    : ''
  const avatar = getAvatarFromTimeline(timelineItem, t, itsMe)

  return { id, title: date, avatarData: avatar, status }
}

const getIdentificationCard = ({ taskState, isOffice, timelineBadge, tenantName }: IdentificationCardProps) => {
  const replaceBadgeByIdentificationCard =
    taskState === TASK_STATES.todo && !isOffice && timelineBadge === TimelineItemContentBadgeTypeT.identificationStarted
  const identificationCard = { tenantName } as TimelineItemContentIdentificationT

  return replaceBadgeByIdentificationCard ? identificationCard : undefined
}

export const getTimelineContent = ({
  timelineItem,
  isOffice,
  formTaskUuid,
  task,
  t,
}: TimelineContentProps): TimelineItemContentT => {
  const tenantName = task.person?.tenant?.name ?? ''
  const taskState = task.statusFe as TaskState

  return {
    document: timelineItem.document
      ? ({ ...timelineItem.document, tenant: { name: tenantName } } as DocumentT | undefined)
      : undefined,
    tenantName,
    badge: timelineItem.badge as TimelineItemContentBadgeTypeT,
    text: timelineItem.comment ?? undefined,
    datetime: timelineItem.insertedAt ?? undefined,
    identificationCard: getIdentificationCard({
      taskState,
      isOffice,
      timelineBadge: timelineItem.badge ?? undefined,
      tenantName: timelineItem.tenantName ?? '',
    }),
    formTaskUuid,
    absenceDetails: getAbsenceDetails({ task, t, timelineItem }),
  }
}

export const getTimelineReminder = ({ badgeType, t }: GetReminderProps): TimelineReminderProps | undefined => {
  const reminderMessage = (() => {
    switch (badgeType) {
      case TimelineItemContentBadgeTypeT.automatedReminder:
        return t('timeline.badge.sentAutomatedReminder')
      case TimelineItemContentBadgeTypeT.reminder:
        return t('timeline.badge.sentAReminder')
      default:
        return undefined
    }
  })()

  const isReminder =
    badgeType &&
    [TimelineItemContentBadgeTypeT.reminder, TimelineItemContentBadgeTypeT.automatedReminder].includes(badgeType) &&
    reminderMessage

  return isReminder
    ? {
        reminder: reminderMessage,
      }
    : undefined
}
